import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Library, Stamp, ShieldCheck, Ban, Clock, Siren, ShieldAlert, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/Card";
import { Severity, SEVERITIES } from "../components/Severity";

const Kpi = ({ icon: Icon, label, value, testId, delay }) => (
  <Card className="rise" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-start justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className="h-5 w-5 text-sky-600" aria-hidden="true" />
    </div>
    <p data-testid={testId} className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
  </Card>
);

const SevList = ({ title, data, testId }) => (
  <Card>
    <h3 className="mb-4 text-base font-semibold text-slate-900">{title}</h3>
    <ul className="space-y-3" data-testid={testId}>
      {SEVERITIES.map((s) => (
        <li key={s} className="flex items-center justify-between">
          <Severity value={s} />
          <span className="font-mono text-sm font-semibold text-slate-800">{data?.[s] || 0}</span>
        </li>
      ))}
    </ul>
  </Card>
);

export default function Dashboard() {
  const { t, tv } = useI18n();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-slate-500">{t("loading")}</p>;

  const domains = Object.entries(d.use_cases_by_domain).map(([k, v]) => ({ name: k, value: v })).sort((a, b) => b.value - a.value);
  const tasks = Object.entries(d.tasks_by_status).map(([k, v]) => ({ name: tv(k), value: v }));
  const kri = [
    { icon: Clock, label: t("overdueTasks"), v: d.kri.overdue_tasks, id: "kri-overdue-tasks" },
    { icon: Siren, label: t("criticalInc"), v: d.kri.critical_incidents_open, id: "kri-critical-incidents" },
    { icon: ShieldAlert, label: t("highRisks"), v: d.kri.high_risks_open, id: "kri-high-risks" },
    { icon: Building2, label: t("vendorsOverdue"), v: d.kri.vendors_overdue, id: "kri-vendors-overdue" },
  ];

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-slate-900 p-6 text-white shadow-sm sm:p-8">
        <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=60" alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-300">{user.role}</p>
          <h1 data-testid="page-title" className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{t("nav_dashboard")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-300 sm:text-base">{t("dashSub")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Library} label={t("kpiUseCases")} value={d.use_cases_total} testId="kpi-use-cases" delay={0} />
        <Kpi icon={Stamp} label={t("kpiPending")} value={d.approvals_pending} testId="kpi-pending-approvals" delay={60} />
        <Kpi icon={ShieldCheck} label={t("kpiControls")} value={`${d.control_effectiveness_pct}%`} testId="kpi-control-effectiveness" delay={120} />
        <Kpi icon={Ban} label={t("kpiDenied")} value={d.kri.denied_access_attempts} testId="kpi-denied-attempts" delay={180} />
      </div>
      <Card>
        <h3 className="mb-4 text-base font-semibold text-slate-900">{t("kri")}</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {kri.map((k) => (
            <div key={k.id} className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
              <k.icon className="h-5 w-5 shrink-0 text-slate-600" aria-hidden="true" />
              <div><p data-testid={k.id} className="text-xl font-bold text-slate-900">{k.v}</p><p className="text-xs text-slate-500">{k.label}</p></div>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h3 className="mb-4 text-base font-semibold text-slate-900">{t("byDomain")}</h3>
          <div className="h-80" data-testid="chart-use-cases-by-domain">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={domains} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} tickFormatter={(v) => tv(v)} />
                <Tooltip />
                <Bar dataKey="value" fill="#0284C7" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <h3 className="mb-4 text-base font-semibold text-slate-900">{t("tasksByStatus")}</h3>
          <div className="h-80" data-testid="chart-tasks-by-status">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tasks}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#0F172A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <SevList title={t("risksBySev")} data={d.risks_by_severity} testId="risks-by-severity" />
        <SevList title={t("incBySev")} data={d.incidents_by_severity} testId="incidents-by-severity" />
      </div>
    </div>
  );
}
